import React, { useEffect, useRef, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Form } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PrevailingWageForm } from './Form';
import PrevailingWageTable, { prevailingWageColumns } from './PrevailingWageTable';
import Excel from '../../../assets/excel.png';
import {
  Breadcrumbs,
  Button,
  EdgeReportingWindow,
  ModalConfirmation,
  SubHeader,
  ReportLoader,
} from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { userHasAccess, generateReport, getReportData, removeReportData, getList } from '../operations';

import './styles.scoped.css';

// Build CSV column defs from the first row's keys so the Payroll Summary export (which
// is not rendered on screen) doesn't need its columns hardcoded — it ships whatever
// tb_sp_cl_proscore_payroll_summary returns.
const columnsFromKeys = rows => (rows?.length ? Object.keys(rows[0]).map(key => ({ Header: key, accessor: key })) : []);

// The customer-list endpoint (tb_sp_cl_proscore_customers via dqf-apiv2 /proscore-customers)
// returns rows shaped { CUST_ID, CUST_NAME, AMC, ... }; normalize to react-select shape.
// CUST_NAME is the option value. Client Name is multi-select: the selected CUST_NAMEs are
// comma-joined into form.ClientName and sent as @CustomerName, which the report procs split
// and LIKE-prefix match per name (see CP-800 proc spec).
const toClientOption = item => {
  const name =
    typeof item === 'string' ? item : (item?.CUST_NAME ?? item?.CustomerName ?? item?.customerName ?? item?.name ?? '');
  return { label: name, value: name };
};

const PrevailingWageCriteria = ({
  form,
  setForm,
  handleChange,
  handleViewResults,
  isLoadingClients,
  clientOptions,
}) => {
  const navigate = useNavigate();
  return (
    <Form
      noValidate
      onSubmit={e => e.preventDefault()}
      style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PrevailingWageForm
        form={form}
        setForm={setForm}
        handleChange={handleChange}
        isLoadingClients={isLoadingClients}
        clientOptions={clientOptions}
      />
      <div className="buttons-row">
        <Button
          variant="primary"
          disabled={!form?.PeriodEndDate || !form?.ClientName}
          onPress={() => handleViewResults()}
          style={{ marginRight: '20px', width: '100px' }}
          text="View"
        />
        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </Form>
  );
};

// Cut Off Date defaults to Week Ending + 9 days (ProScore convention); user-overridable.
// Parse by component (not new Date(iso)) to avoid the UTC-shift that moves the date a day.
export const addDays = (isoDate, days) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  const pad = n => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const PrevailingWage = () => {
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const navigate = useNavigate();
  const [form, setForm] = useState({ PeriodEndDate: '', ClientName: '', CutOffDate: '', Exception: 'A' });
  const [data, setData] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  // One "Export" control drives both CSVs via hidden CSVLinks (Timesheet + Payroll Summary).
  const timesheetCsvRef = useRef(null);
  const payrollCsvRef = useRef(null);

  useEffect(() => {
    if (menuItems) {
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  // Client Name pick list source — CP-800 sources it from tb_sp_cl_proscore_customers.
  // Backend dependency: the 'proscore-customers' REST endpoint must exist server-side.
  useEffect(() => {
    let active = true;
    setIsLoadingClients(true);
    getList('proscore-customers')
      .then(res => {
        if (active && Array.isArray(res)) setClientOptions(res.map(toClientOption).filter(o => o.value));
      })
      .finally(() => {
        if (active) setIsLoadingClients(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    // Selecting a Period End Date auto-fills Cut Off Date to +9 days; the user can still override it.
    if (name === 'PeriodEndDate') {
      setForm(prev => ({ ...prev, PeriodEndDate: value, CutOffDate: addDays(value, 9) }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const timesheetFileName = `edge-reporting-prevailing-wage-timesheet-${newDateFormatted.toString()}.csv`;
  const payrollSummaryFileName = `edge-reporting-prevailing-wage-payroll-summary-${newDateFormatted.toString()}.csv`;

  // Single Export action downloads both files: Timesheet always, Payroll Summary when present.
  // The browser may ask to allow multiple downloads the first time.
  const handleExport = () => {
    timesheetCsvRef.current?.link?.click();
    if (payrollSummary?.length) payrollCsvRef.current?.link?.click();
  };

  // Export is gated behind an approval modal so the user knows two files are coming.
  const confirmExport = () => {
    setShowExportModal(false);
    handleExport();
  };

  const handleViewResults = async () => {
    if (!form.PeriodEndDate || !form.ClientName) {
      alert('Period End Date and Client Name are required.');
      return;
    }

    setLoadingResults(true);

    // Criteria map to the ProScore proc params (@WeekEnding, @CustomerName, @CutoffDate,
    // @Exception). @CustomerName is a comma-separated list of CUST_NAMEs (multi-select) the proc
    // splits. @Exception comes from the Exception Type pick list (A = all, N = on-time, Y = late).
    const payload = {
      WeekEnding: form.PeriodEndDate,
      CustomerName: form.ClientName,
      CutoffDate: form.CutOffDate || '',
      Exception: form.Exception || 'A',
    };

    try {
      // Timesheet (shown) and Payroll Summary (export only) come from two separate procs.
      const [timesheetRes, payrollRes] = await Promise.all([
        generateReport({ endpoint: 'proscore-timesheet', payload }),
        generateReport({ endpoint: 'proscore-payroll-summary', payload }),
      ]);

      const timesheetData = timesheetRes?.location ? await getReportData(timesheetRes.location) : [];
      const payrollData = payrollRes?.location ? await getReportData(payrollRes.location) : [];

      if (timesheetRes?.location) await removeReportData(timesheetRes.location);
      if (payrollRes?.location) await removeReportData(payrollRes.location);

      if (timesheetData?.length) {
        setData(timesheetData);
        setPayrollSummary(Array.isArray(payrollData) ? payrollData : []);
        setShowResults(true);
      } else {
        alert(timesheetRes?.statusDescription || 'No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
      alert('An error occurred while generating the report. Please try again.');
    } finally {
      setLoadingResults(false);
    }
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Prevailing Wage Report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Prevailing Wage'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'View Report' : 'Prevailing Wage'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <div className="export-links">
            {/* Hidden CSVLinks perform the downloads; the visible Export control triggers both. */}
            <CSVLink
              ref={timesheetCsvRef}
              className="export-hidden"
              filename={timesheetFileName}
              data={getCSVData(data, prevailingWageColumns)}
            />
            <CSVLink
              ref={payrollCsvRef}
              className="export-hidden"
              filename={payrollSummaryFileName}
              data={getCSVData(payrollSummary, columnsFromKeys(payrollSummary))}
            />
            <button
              type="button"
              className="export-button"
              aria-label="Export"
              onClick={() => setShowExportModal(true)}>
              <ImageColorChanger src={Excel} className="export-icon" alt="export-prevailing-wage-to-csv" />
            </button>
            <ModalConfirmation
              isOpen={showExportModal}
              title="Export report files?"
              body="This will download two files — the Timesheet and the Payroll Summary."
              primaryButton
              primaryButtonTitle="Download"
              onClick={confirmExport}
              optionalClose
              secondaryButtonTitle="Cancel"
              secondaryOnClick={() => setShowExportModal(false)}
            />
          </div>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <PrevailingWageTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button
              variant="primary"
              onPress={() => setShowResults(false)}
              style={{ marginRight: '20px', width: '100px' }}
              text="Back"
            />
            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{ border: '2px solid #343a40', minHeight: 200 }}
          windowContent={
            <PrevailingWageCriteria
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              handleViewResults={handleViewResults}
              isLoadingClients={isLoadingClients}
              clientOptions={clientOptions}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default PrevailingWage;
